import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Plug } from "lucide-react";

import { agentSettingsQueryOptions } from "@/agent/chat/models";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/** Claude's Customize > Connectors page, where "Add custom connector" takes the URL. */
export const CLAUDE_CONNECTORS_URL = "https://claude.ai/customize/connectors";
const COPIED_MS = 2000;

/**
 * /profile: the MCP server's URL for connecting an AI app (Claude, ChatGPT...), in full, with Copy
 * and Add to Claude. Hidden while mcp_url is null (MCP connections off, or no usable public URL).
 */
const AiAppsCard = () => {
  const { data } = useQuery(agentSettingsQueryOptions);
  const url = data?.mcp_url;
  const field = useRef<HTMLElement>(null);
  // What the last press did: copied it, or selected it where the clipboard is refused.
  const [outcome, setOutcome] = useState<"copied" | "selected" | null>(null);
  const copied = outcome === "copied";

  useEffect(() => {
    if (!outcome) return;
    const timer = window.setTimeout(() => setOutcome(null), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [outcome]);

  if (!url) return null;

  // Where the clipboard is refused (no permission, not a secure page), the URL is selected instead.
  const selectUrl = () => {
    const selection = window.getSelection();
    if (!field.current || !selection) return;
    const range = document.createRange();
    range.selectNodeContents(field.current);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const copy = async () => {
    try {
      if (!navigator.clipboard) throw new Error("No clipboard");
      await navigator.clipboard.writeText(url);
      setOutcome("copied");
    } catch {
      selectUrl();
      setOutcome("selected");
    }
  };

  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          AI apps
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* One line for URLs up to about 100 characters; a longer one wraps (break-all). On a phone the
            URL takes the full width and Copy goes below it. */}
        <div
          role="group"
          aria-label="Server URL"
          className="flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm max-sm:flex-col max-sm:items-stretch"
        >
          <code ref={field} className="min-w-0 flex-1 select-all break-all font-mono text-sm">
            {url}
          </code>
          <Button variant="outline" className="shrink-0 max-sm:h-10" onClick={() => void copy()}>
            {copied ? (
              <Check className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {copied ? "Copied" : "Copy"}
            <span className="sr-only"> server URL</span>
          </Button>
        </div>
        <span role="status" className="sr-only">
          {outcome === "copied" ? "Copied" : outcome === "selected" ? "URL selected" : ""}
        </span>
        <Button asChild className="max-sm:h-10">
          <a href={CLAUDE_CONNECTORS_URL} target="_blank" rel="noopener noreferrer">
            Add to Claude
          </a>
        </Button>
      </CardContent>
    </Card>
  );
};

export default AiAppsCard;
